/**
 * Host (TV) shell. Subscribes to the room document and — once the room is
 * active — also to `rooms/{code}/state/main`. Switches between the lobby
 * UI and the game module's HostGameView based on `room.status`.
 */

import { Box, Button, Loader, Paper, Stack, Text, Title } from '@mantine/core'
import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { getGameModule } from '../registry'
import { getDb } from '@/platform/firebase/client'
import { HostLobby } from '../lobby/HostLobby'
import { subscribeRoom, subscribeRoomState } from '../lobby/service'
import type { GameId, Room } from '@mdev/common'
import type { GameModule } from '../module'

const db = getDb()

/**
 * Sentinel uid passed to `gameModule.subscribePlayerState` from the host
 * shell. The host has no real uid; modules with private docs (e.g. SY's
 * `private/mrx`) use the uid to gate which secrets they include in the
 * merged TState. This sentinel is guaranteed to never match a real player
 * uid (which are uuidv4 strings), so the host always receives the
 * "no-secrets" view of the game.
 */
const HOST_VIEWER_UID = '__host__'

type RoomFetchState =
  | { kind: 'loading' }
  | { kind: 'not-found' }
  | { kind: 'ready'; room: Room<unknown> }

export function HostShell() {
  const { gameId, code } = useParams<{ gameId: string; code: string }>()

  const [ roomState, setRoomState ] = useState<RoomFetchState>( { kind: 'loading' } )
  const [ gameStateData, setGameStateData ] = useState<unknown>( null )

  // Look up the game module — error early if unknown.
  const gameModule: GameModule<unknown, unknown> | undefined = gameId
    ? getGameModule( gameId as GameId )
    : undefined

  // Subscribe to the room document.
  useEffect( () => {
    if ( !code ) return
    const unsub = subscribeRoom<unknown>( code, ( room ) => {
      if ( room === null ) {
        setRoomState( { kind: 'not-found' } )
        return
      }
      setRoomState( { kind: 'ready', room } )
    } )
    return unsub
  }, [ code ] )

  // Subscribe to the game-state subdoc while the room is active OR finished
  // — finished games still need the public state to render a per-game
  // end-game view (e.g. Scotland Yard's replay map + scrubber). We use the
  // same `subscribePlayerState` hook the player shell uses, with a sentinel
  // uid that never matches a real player. This ensures the host's view is
  // structurally identical to a non-secret-holder's view (e.g. for Scotland
  // Yard, the host receives `{ public, mrxStation: -1 }`). At game-end the
  // public state contains a denormalized `replay` field with everyone's
  // station history, so the host doesn't need a private subscription.
  const room = roomState.kind === 'ready' ? roomState.room : null
  const status = room?.status
  useEffect( () => {
    if ( !code ) return
    if ( status !== 'active' && status !== 'finished' ) {
      setGameStateData( null )
      return
    }
    if ( gameModule?.subscribePlayerState ) {
      const unsub = gameModule.subscribePlayerState( db, code, HOST_VIEWER_UID, ( s ) => {
        setGameStateData( s )
      } )
      return unsub
    }
    const unsub = subscribeRoomState<unknown>( code, ( s ) => {
      setGameStateData( s )
    } )
    return unsub
  }, [ code, status, gameModule ] )

  // --- Error / loading states ---------------------------------------------

  if ( !gameId || !code ) {
    return (
      <ShellMessage
        title="Bad URL"
        body="Missing game id or room code."
      />
    )
  }

  if ( !gameModule ) {
    return (
      <ShellMessage
        title="Unknown game"
        body={ `No game registered for "${ gameId }".` }
      />
    )
  }

  if ( roomState.kind === 'loading' ) {
    return (
      <Box
        style={ {
          minHeight: '100vh',
          background: '#0B0F1A',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        } }
      >
        <Stack
          align="center"
          gap="md"
        >
          <Loader color="yellow" />
          <Text c="dimmed">Loading room { code }…</Text>
        </Stack>
      </Box>
    )
  }

  if ( roomState.kind === 'not-found' ) {
    return (
      <ShellMessage
        title="Room not found"
        body={ `No room with code "${ code }".` }
      />
    )
  }

  const liveRoom = roomState.room

  if ( liveRoom.gameId !== gameModule.id ) {
    return (
      <ShellMessage
        title="Game mismatch"
        body={ `Room ${ code } is for ${ liveRoom.gameId }, not ${ gameModule.id }.` }
      />
    )
  }

  // --- Render based on room status ----------------------------------------

  if ( liveRoom.status === 'lobby' ) {
    return (
      <HostLobby
        room={ liveRoom }
        gameModule={ gameModule }
        isHost={ true }
      />
    )
  }

  if ( liveRoom.status === 'active' ) {
    if ( gameStateData === null ) {
      return (
        <Box
          style={ {
            minHeight: '100vh',
            background: '#0B0F1A',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          } }
        >
          <Stack
            align="center"
            gap="md"
          >
            <Loader color="yellow" />
            <Text c="dimmed">Loading game state…</Text>
          </Stack>
        </Box>
      )
    }
    const HostGameView = gameModule.HostGameView
    return (
      <HostGameView
        room={ liveRoom }
        state={ gameStateData }
      />
    )
  }

  // status === 'finished'
  // If the game module provides its own end-game view, prefer it (e.g. SY's
  // HostFinishedView with replay map + scrubber). Fall back to the platform's
  // generic FinishedView for games that don't bother. The custom view also
  // needs the public state, so we wait for it to arrive before rendering;
  // legacy games (which never wrote a state doc) hit the fallback below.
  if ( gameModule.HostFinishedView && gameStateData !== null ) {
    const HostFinishedView = gameModule.HostFinishedView
    return (
      <HostFinishedView
        room={ liveRoom }
        state={ gameStateData }
      />
    )
  }
  return (
    <FinishedView
      room={ liveRoom }
      gameModule={ gameModule }
    />
  )
}

function FinishedView( props: {
  room: Room<unknown>
  gameModule: GameModule<unknown, unknown>
} ) {
  const { room, gameModule } = props
  const winner = room.winner
  return (
    <Box
      style={ {
        minHeight: '100vh',
        background: '#0B0F1A',
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      } }
      p="xl"
    >
      <Paper
        p="xl"
        withBorder
        style={ { borderColor: '#1F2937', background: '#0F172A', minWidth: 360 } }
      >
        <Stack
          gap="md"
          align="center"
        >
          <Title
            order={ 2 }
            c="white"
          >
            Game over
          </Title>
          { winner ? (
            <>
              <Text
                size="xl"
                c="yellow"
                fw={ 700 }
                tt="capitalize"
              >
                { winner.team } win!
              </Text>
              <Text
                c="dimmed"
                ta="center"
              >
                { winner.reason }
              </Text>
            </>
          ) : (
            <Text c="dimmed">No winner recorded.</Text>
          ) }
          <Button
            component={ Link }
            to={ `/games/create/${ gameModule.id }` }
            color="yellow"
          >
            Play again
          </Button>
        </Stack>
      </Paper>
    </Box>
  )
}

function ShellMessage( props: { title: string; body: string } ) {
  return (
    <Box
      style={ {
        minHeight: '100vh',
        background: '#0B0F1A',
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      } }
      p="xl"
    >
      <Stack
        gap="md"
        align="center"
      >
        <Title
          order={ 2 }
          c="white"
        >
          { props.title }
        </Title>
        <Text c="dimmed">{ props.body }</Text>
        <Button
          component={ Link }
          to="/games"
          variant="light"
        >
          Back to games
        </Button>
      </Stack>
    </Box>
  )
}
